import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import type { PhotoCard } from '@photos/shared';
import { useDeleteFlockPhoto, useFlockTags, usePhoto } from '../api/hooks';
import { useDownloads } from '../hooks/useDownloads';
import { fmtLongDateTime, fmtMB, wallClock } from '../lib/format';
import { VISIBILITY_LOOK } from '../lib/visibility';
import { useSession } from '../state/session';
import { useToast } from '../state/toasts';
import { RemovalDialog } from './dialogs/RemovalDialog';
import { ShareDialog } from './dialogs/ShareDialog';
import { fromFlock } from './PhotoGrid';
import { Icon } from './Icon';
import { LightboxFrame } from './LightboxFrame';
import { TagEditor } from './TagEditor';

interface LightboxProps {
  photos: PhotoCard[];
  /** size of the whole result set (more pages may load as you go) */
  total?: number;
  index: number;
  onIndex(i: number): void;
  onClose(): void;
  /** load more results when nearing the end */
  onNearEnd?(): void;
  /** a tag chip was clicked: search that tag */
  onSearchTag(tag: string): void;
}

/** Browse lightbox: for looking and downloading. Editing lives in Studio (except a poster's own Flock posts). */
export function Lightbox({ photos, total, index, onIndex, onClose, onNearEnd, onSearchTag }: LightboxProps) {
  const p = photos[index];
  const { access, tagIndex } = useSession();
  const detail = usePhoto(p?.id);
  const flockTags = useFlockTags();
  const deletePost = useDeleteFlockPhoto();
  const toast = useToast();
  const { downloadOne } = useDownloads();
  const [dialog, setDialog] = useState<'share' | 'removal' | null>(null);
  const [tags, setTags] = useState<string[]>(p?.tags ?? []);

  useEffect(() => setTags(detail.data?.tags ?? p?.tags ?? []), [p?.id, p?.tags, detail.data?.tags]);

  const title = p ? tagIndex.eventLabel(p.tags) || p.fileName : '';
  const ownPost = !!p && access.canEditFlockPost(p);
  const inStudio = !!p?.homeAlbumId && p.source === 'glean' && access.canOpenAlbum(p.homeAlbumId);

  const saveTags = (next: string[]) => {
    if (!p) return;
    const prev = tags;
    setTags(next);
    flockTags.mutate(
      { id: p.id, addTags: next.filter((t) => !prev.includes(t)), removeTags: prev.filter((t) => !next.includes(t)) },
      {
        onError: (e) => {
          setTags(prev);
          toast({ icon: 'info', title: 'Couldn’t save tags', sub: e.message });
        },
      },
    );
  };

  const remove = () => {
    if (!p) return;
    deletePost.mutate(p.id, {
      onSuccess: () => {
        toast({ icon: 'trash', title: 'Photo deleted', sub: 'It was removed from your Flock post.' });
        onClose();
      },
      onError: (e) => toast({ icon: 'info', title: 'Couldn’t delete the photo', sub: e.message }),
    });
  };

  return (
    <LightboxFrame photos={photos} total={total} index={index} title={title} onIndex={onIndex} onClose={onClose} onNearEnd={onNearEnd} paused={!!dialog}>
      {p && (
        <>
          <h3>{title}</h3>
          <div className="sub">{fmtLongDateTime(wallClock(p.takenAt))}</div>
          {(p.visibility === 'restricted' || p.picked || fromFlock(p)) && (
            <div className="sec" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {p.visibility === 'restricted' && (
                <span className="pill" style={{ color: VISIBILITY_LOOK.restricted.color }} title={VISIBILITY_LOOK.restricted.description}>
                  <Icon name="lock" />
                  Restricted · don’t share
                </span>
              )}
              {p.picked && (
                <span className="pill" style={{ color: 'var(--gold)' }}>
                  <Icon name="star" />
                  Pick
                </span>
              )}
              {fromFlock(p) && (
                <span className="pill" style={{ color: '#b2620f' }}>
                  <Icon name="flock" />
                  {p.source === 'flock' ? 'Flock post' : 'Added from Flock'}
                </span>
              )}
            </div>
          )}
          {p.caption && <p style={{ margin: '12px 0 0' }}>“{p.caption}”</p>}
          {(detail.data?.albums.length ?? 0) > 0 && (
            <div className="sec">
              <div className="lbl">Albums</div>
              <div className="albumlist">
                {detail.data!.albums.map((a) => (
                  <div key={a.id} className="albumrow">
                    <Icon name="folder" />
                    {access.canOpenAlbum(a.id) ? <Link to={`/studio/albums/${a.id}`}>{a.name}</Link> : <span>{a.name}</span>}
                    {a.home && <span className="pill">Home</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="sec">
            <div className="lbl">Download</div>
            <div className="acts">
              <button type="button" className="btn primary" onClick={() => downloadOne(p.id, 'original')}>
                <Icon name="dl" />
                Original · {fmtMB(p.bytes)} MB
              </button>
              <div className="row2">
                <button type="button" className="btn" onClick={() => downloadOne(p.id, 'large')}>
                  Large 2048
                </button>
                <button type="button" className="btn" onClick={() => downloadOne(p.id, 'social')}>
                  Social 1080
                </button>
              </div>
            </div>
          </div>
          <div className="sec">
            <div className="lbl">Tags</div>
            {ownPost ? (
              <TagEditor className="tagbox" id="lbTags" tags={tags} onChange={saveTags} />
            ) : (
              <div className="tagbox" id="lbTags">
                {tags.map((t) => (
                  <button type="button" key={t} className={`chip cat-${tagIndex.catOf(t)}`} style={{ border: 0 }} onClick={() => onSearchTag(t)}>
                    #{t}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="sec">
            <div className="lbl">Details</div>
            <dl className="kv">
              <dt>{p.source === 'flock' ? 'Posted by' : 'Uploaded by'}</dt>
              <dd>{p.uploaderName}</dd>
              <dt>Camera</dt>
              <dd>{detail.data?.camera || '—'}</dd>
              <dt>Dimensions</dt>
              <dd>
                {p.width} × {p.height}
              </dd>
              <dt>File</dt>
              <dd>
                {p.fileName} · {fmtMB(p.bytes)} MB
              </dd>
            </dl>
          </div>
          <div className="sec">
            <div className="lbl">More</div>
            <div className="acts">
              {access.canShare(p) && (
                <button type="button" className="btn" onClick={() => setDialog('share')}>
                  <Icon name="share" />
                  Share link
                </button>
              )}
              {inStudio && (
                <Link className="btn" to={`/studio/albums/${encodeURIComponent(p.homeAlbumId!)}?photo=${encodeURIComponent(p.id)}`}>
                  <Icon name="settings" />
                  Open in Studio
                </Link>
              )}
              {ownPost ? (
                <button type="button" className="btn danger" onClick={remove} disabled={deletePost.isPending}>
                  <Icon name="trash" />
                  Delete from my post
                </button>
              ) : (
                <button type="button" className="btn sm warn" onClick={() => setDialog('removal')}>
                  <Icon name="flag" />
                  Request removal of this photo
                </button>
              )}
            </div>
          </div>
          {dialog === 'share' && <ShareDialog photos={[p]} onClose={() => setDialog(null)} />}
          {dialog === 'removal' && <RemovalDialog photoId={p.id} onClose={() => setDialog(null)} onDone={onClose} />}
        </>
      )}
    </LightboxFrame>
  );
}
